import { useEffect, useRef, useState } from 'react'
import { NavLink } from 'react-router-dom'
import Icon from '../components/Icon'
import { getTokenPayload } from '../api/security'
import { useIsMobile } from '../hooks/useIsMobile'
import styles from './Sidebar.module.css'

const NAV_ITEMS = [
  { label: 'Home', path: '/dashboard', icon: 'home' },
  { label: 'Skills', path: '/skills', icon: 'skills' },
  { label: 'Teams', path: '/teams', icon: 'people' },
  { label: 'Cases', path: '/cases', icon: 'cases' },
  { label: 'Knowledge', path: '/knowledge', icon: 'knowledge' },
  // Only superadmin_right sees Connect — not admin_right, not org/team skill
  // management. See pages/connectors.jsx for the matching direct-URL guard.
  { label: 'Connect', path: '/connect', icon: 'connect', superAdminOnly: true },
  { label: 'Administration', path: '/admin', icon: 'settings', adminOnly: true },
]

export default function Sidebar({ open = false, onClose }) {
  const [collapsed, setCollapsed] = useState(false)
  const firstNavLinkRef = useRef(null)
  const isMobile = useIsMobile()
  // Desktop's manual collapse (icon-only rail) doesn't apply to the mobile
  // overlay drawer — always show full labels there, regardless of whatever
  // `collapsed` was left at on desktop.
  const showCollapsed = collapsed && !isMobile
  const rights = getTokenPayload()?.rights
  const isSuperAdmin = Boolean(rights?.superadmin_right)
  // superadmin_right overrides every granular right, admin_right included.
  const isAdmin = Boolean(rights?.admin_right) || isSuperAdmin
  const items = NAV_ITEMS.filter(item =>
    (!item.adminOnly || isAdmin) && (!item.superAdminOnly || isSuperAdmin)
  )

  // Move focus into the drawer when it opens (mobile only — desktop never
  // sets `open`, since Navbar's hamburger trigger is hidden above 600px).
  useEffect(() => {
    if (open) firstNavLinkRef.current?.focus()
  }, [open])

  return (
    <>
      {/* Mobile-only drawer backdrop — no-op on desktop (see Sidebar.module.css) */}
      <div
        className={`${styles.backdrop} ${open ? styles.backdropOpen : ''}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`${styles.sidebar} ${showCollapsed ? styles.collapsed : ''} ${open ? styles.drawerOpen : ''}`}
      >

        {/* Logo */}
        <div className={styles.logoRow}>
          <img src="/GaussIQlogo.png" alt="GaussIQ" className={styles.logoImage} />
          {!showCollapsed && <span className={styles.logoName}>GaussIQ</span>}
          {!isMobile && (
            <button
              className={styles.collapseBtn}
              onClick={() => setCollapsed(prev => !prev)}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <Icon name={collapsed ? 'sidebar-expand' : 'sidebar-collapse'} />
            </button>
          )}
        </div>

        {/* Nav items */}
        <nav className={styles.nav}>
          {items.map((item, index) => (
            <NavLink
              key={item.path}
              ref={index === 0 ? firstNavLinkRef : undefined}
              to={item.path}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
              title={showCollapsed ? item.label : undefined}
            >
              <span className={styles.navIcon}><Icon name={item.icon} /></span>
              {!showCollapsed && <span className={styles.navLabel}>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

      </aside>
    </>
  )
}
